<template>
  <b-dropdown
    :inline="inline"
    :value="isOpened"
    :height="height"
    :position="position"
    :show-shadow="showShadow"
    ref="dropdown"
    :class="disabled && $style['dropdown--disabled']"
    @input="onToggle"
    @scroll="$emit('scroll', $event)"
  >
    <template #toggle="{ isOpened }">
      <div
        :class="$style.toggle"
        :data-testid="`select-dropdown-${name}`"
        @keydown.enter.stop="handleEnterKey"
        @keydown.up.prevent="handleUpKey"
        @keydown.down.prevent="handleDownKey"
        @keydown.tab="handleTabKeyDown"
        @keyup.tab="handleTabKeyUp"
      >
        <slot
          name="toggle"
          :selected-item="selectedItem"
          :is-opened="isOpened"
          :handle-query-blur="handleQueryBlur"
        >
          <bvc-input
            :id="id"
            :label="label"
            :placeholder="placeholder"
            :size="size"
            :disabled="disabled"
            :minWidth="minWidth"
            slotTheme="blank"
            :invalid="invalid"
            :value="displayValue"
            :autocomplete="autocomplete"
            :readonly="!searchable || readonly"
            :inputmode="inputmode"
            :name="name"
            :keep-focus="keepFocus"
            ref="input"
            @input="inputQuery"
            @blur="handleBlur"
            :data-testid="`select-input-${name}`"
          >
            <template #left v-if="$slots.valueIcon">
              <slot name="valueIcon" />
            </template>
            <template #right>
              <i
                :class="[
                  isOpened ? $style['toggleIcon--opened'] : $style.toggleIcon,
                  disabled && $style['toggleIcon--disabled']
                ]"
                :data-testid="`select-input-toggle-${name}`"
              />
            </template>
          </bvc-input>
        </slot>
      </div>
    </template>

    <template #default>
      <slot name="content">
        <ul
          v-if="filteredOptions.length"
          :class="[$style.defaultList, 'list']"
          ref="list"
        >
          <li
            v-for="(item, index) in filteredOptions"
            :key="item.id || index"
            @click="selectItem(item)"
            :data-testid="`dropdown-option-${item.testId || item.value}`"
          >
            <div
              :class="[
                $style.defaultItem,
                index == filteredOptions.length - 1 && $style.defaultItemLast,
                index == 0 && $style.defaultItemFirst,
                isSelected(item) && $style['--selected'],
                item.disabled && $style['--disabled'],
                !touchable && highlightItemIndex === index && $style['--highlighted']
              ]"
              :ref="highlightItemIndex === index ? 'highlightItem' : undefined"
              @mouseenter="highlightItemIndex = index"
            >
              <div :class="$style.defaultItemInner">
                <slot
                  name="item"
                  :item="item"
                  :selected="isSelected(item)"
                  :is-first="index == 0"
                  :is-last="index == filteredOptions.length - 1"
                >
                  {{ item.label }}
                </slot>
              </div>
            </div>
          </li>
        </ul>

        <b-loader v-if="loading" :class="$style.loader"/>

        <template v-else>
          <b-empty-placeholder
            v-if="showEmptyPlaceholder"
            :class="$style.empty"
            type="generic"
            :title="$t('no-results')"
            :description="emptyDescription"
          />
          <b-empty-placeholder
            v-if="showTypingPlaceholder"
            :class="$style.empty"
            type="generic"
            :title="$t('start-typing')"
          />
        </template>
      </slot>
    </template>

  </b-dropdown>
</template>

<script>
/*
 *
 * b-select component
 *
 */
import { is } from 'ramda'

import BDropdown from '../b-dropdown/index.vue'
import BvcInput from '@/booksy-vue-components/components/b-input/index.vue'

import Input from '../../mixins/input.js'
import InputSize from '../../mixins/input-size.js'
import VModelInterface from '../../mixins/v-model-interface.js'
import { useDeviceDetect } from '@/composables/device-detect/index.js'

import { useEppo } from '@/composables/eppo/index.js'
import { FEATURE_FLAGS } from '@/lib/consts.js'
import { computed } from 'vue'
import { useVModel } from '@vueuse/core'

export default {

  name: 'b-select',

  mixins: [
    Input,
    InputSize,
    VModelInterface,
  ],

  components: {
    BDropdown,
    BvcInput,
  },

  props: {

    label: {
      type: String,
      default: '',
    },

    placeholder: {
      type: String,
      default: '',
    },

    /*
     * options
     * @property {string}        options.label label
     * @property {string,number} options.value value
     */
    options: {
      type: Array,
      required: true,
    },

    /*
     * inline (mode)
     */
    inline: {
      type: Boolean,
      default: false,
    },

    /*
     * (scrollable) height
     */
    height: {
      type: [Number, String],
      default: 'auto',
    },

    /*
     * is opened
     * (optional; if set, takes precedense over dropdown internal state)
     */
    forceOpen: {
      type: Boolean,
      default: null,
    },

    clickOutsideCloses: {
      type: Boolean,
      default: true,
    },

    position: {
      type: String,
      default: 'bottom-left',
    },

    minWidth: {
      type: Number,
      default: 0,
    },

    showShadow: {
      type: Boolean,
      default: true,
    },

    invalid: {
      type: Boolean,
      default: false,
    },

    filterable: {
      type: Boolean,
      default: true,
    },

    autocomplete: {
      type: String,
      default: 'off',
    },

    loading: {
      type: Boolean,
      default: false,
    },

    searchable: {
      type: Boolean,
      default: true,
    },

    readonly: {
      type: Boolean,
      default: false,
    },

    inputmode: {
      type: String,
    },

    combobox: {
      type: Boolean,
      default: false,
    },

    modelValue: {
      type: [String, Number],
      default: '',
    },
  },

  emits: ['update:modelValue', 'change', 'input'],

  setup (props, { emit }) {
    const eppo = useEppo()
    const model = useVModel(props, 'modelValue', emit)

    const isNewBvcInput = eppo.ff(FEATURE_FLAGS.Refactor_BvcInput)
    const currentValue = computed(() => {
      return isNewBvcInput ? (props.value || model.value) : props.value
    })

    const { touchable } = useDeviceDetect()

    return {
      model,
      touchable,
      isNewBvcInput,
      currentValue,
    }
  },

  data () {
    return {
      query: null,
      opened: false,
      highlightItemIndex: -1,
    }
  },

  computed: {

    hasOwnInput () {
      return this.$el
    },

    isOpened () {
      // shortcut: force `false`
      if (this.readonly || this.disabled) return false

      // if `this.forceOpen` is set...
      if (is(Boolean, this.forceOpen)) {
        // shortcut: force `this.forceOpen`
        return this.forceOpen
      }

      if (!this.$slots.toggle) {
        return this.opened
      }

      // fallback: do not override dropdown internal state
      return null
    },

    selectedItem () {
      return this.options.find(o => o.value === this.currentValue)
    },

    selectedItemIndex () {
      return this.filteredOptions.findIndex(o => o.value === this.currentValue)
    },

    displayValue () {
      if (this.query !== null) {
        return this.query
      }

      if (this.selectedItem) {
        return this.selectedItem.label
      }

      return this.currentValue
    },

    filteredOptions () {
      return this.filterable && this.query && this.options.length
        ? this.options.filter(({ label }) => {
          return label && label.toString().toLowerCase().includes(this.query.toLowerCase())
        })
        : this.options
    },

    showEmptyPlaceholder () {
      if (!this.filterable) {
        return this.query && !this.filteredOptions.length
      }
      return !this.filteredOptions.length
    },

    showTypingPlaceholder () {
      return !this.filterable && !this.filteredOptions.length && this.query === null
    },

    emptyDescription () {
      return this.searchable ? this.$t('check-your-spelling') : ''
    },

    keepFocus () {
      return this.searchable && this.isOpened
    },
  },

  methods: {

    /*
     *
     * public interface
     *
     */
    open () {
      this.opened = true
      this.$emit('open')

      this.highlightItemIndex = this.selectedItemIndex

      this.$nextTick(() => {
        this.scrollToHighlighted()
      })

      if (this.searchable) {
        this.$refs.input?.select()
      }
    },

    close () {
      this.opened = false
      this.query = null
      this.$refs.input?.blur()
    },

    /*
     *
     * private
     *
     */

    onToggle (isOpened) {
      isOpened ? this.open() : this.close()
      this.$emit('toggle', isOpened)
    },

    selectItem (item) {
      // shortcut
      if (this.readonly || this.disabled || item.disabled) return

      if (this.isNewBvcInput) {
        // Vue 3 pattern
        this.model = item.value
        // emit input to maintain old @input event
        this.$emit('input', item.value)
        this.$emit('change', item.value)
      } else {
        this.emitInput(item.value)
      }

      this.close()
    },

    isSelected (item = {}) {
      return Boolean(this.selectedItem && this.selectedItem.value === item.value)
    },

    // set query value to filter options
    inputQuery (value) {
      this.query = value
      this.$emit('query', value)
    },

    handleBlur () {
      if (this.combobox && this.query) {
        if (this.isNewBvcInput) {
          this.model = this.query
          // emit input to maintain old @input event
          this.$emit('input', this.query)
          this.$emit('change', this.query)
        } else {
          this.emitInput(this.query)
        }
      }
    },

    handleQueryBlur (externalQuery) {
      const queryToMatch = externalQuery || this.query
      if (
        // start only when user typed something to search
        queryToMatch
        // ...and there is only one option matching query
        && this.filteredOptions.length === 1
      ) {
        // select this item on blur
        this.selectItem(this.filteredOptions[0])
      }
    },

    handleEnterKey () {
      if (!this.touchable && this.highlightItemIndex > -1) {
        const item = this.filteredOptions[this.highlightItemIndex]
        if (item) this.selectItem(item)
      }
    },

    handleUpKey () {
      if (this.highlightItemIndex === 0) return

      this.highlightItemIndex -= 1
      this.$nextTick(() => {
        this.scrollToHighlighted()
      })
    },

    handleDownKey () {
      if (this.highlightItemIndex === (this.filteredOptions.length - 1)) return
      this.highlightItemIndex += 1
      this.$nextTick(() => {
        this.scrollToHighlighted()
      })
    },

    handleTabKeyDown () {
      const hasOptions = this.filteredOptions.length
      const isAnyOptionHighlighted = this.highlightItemIndex >= 0
      const isHighlightedValueSameAsCurrent = this.filteredOptions[this.highlightItemIndex]?.label !== this.currentValue

      if (hasOptions && isAnyOptionHighlighted && isHighlightedValueSameAsCurrent) {
        const selectedValue = this.filteredOptions[this.highlightItemIndex].value

        if (this.isNewBvcInput) {
          this.model = selectedValue
          // emit input to maintain old @input event
          this.$emit('input', selectedValue)
          this.$emit('change', selectedValue)
        } else {
          this.emitInput(selectedValue)
        }
      }

      this.close()
    },

    handleTabKeyUp () {
      this.open()
    },

    scrollToHighlighted () {
      const scrollElement = this.$refs.list && this.$refs.list.parentElement
      const selectedOption = this.$refs.highlightItem && this.$refs.highlightItem[0]
      if (selectedOption && scrollElement) {
        scrollElement.scrollTop = (selectedOption.offsetTop + selectedOption.clientHeight / 2) - scrollElement.clientHeight / 2
      }
    },
  },

  watch: {
    query (val) {
      this.$emit('search', val)
      if (val) {
        this.highlightItemIndex = 0
        this.$nextTick(() => {
          this.scrollToHighlighted()
        })
      }
    },
  },
}
</script>

<style lang="scss" module>
.toggleIcon {
  font-size: 28px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  transform-origin: center;
  transition: transform .1s ease-in-out;

  &:before {
    color: $color-gray-08;
    content: '';
    width: 16px;
    height: 16px;
    background: url('../../assets/arrow.svg') center center no-repeat;
    background-size: cover;
  }

  &--opened {
    @extend .toggleIcon;
    transform: rotate(180deg);
    transition: transform .1s ease-in-out;
  }
  &--disabled {
    opacity: .6;
  }
}

.dropdown {
  &--disabled * {
    cursor: default;
  }
}

.defaultItem {
  color: $color-primary;
  font-size: 14px;
  font-weight: normal;
  line-height: 22px;
  padding: 10px 0;
  border-bottom: 1px solid $color-gray-15;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  position: relative;
  z-index: 1;

  &:before {
    content: " ";
    position: absolute;
    top: 0;
    bottom: 0;
    left: -24px;
    right: -24px;
    background-color: $color-gray-14;
    opacity: 0;
    z-index: -1;
  }

  &Inner {
    display: flex;
    align-items: center;
    text-align: left
  }

  // &First {
  //   padding-top: 0;
  // }

  &Last {
    border-bottom: 0;
  }

  &.--selected {
    font-weight: 600;

    &:after {
      $borderWidth: 1px;
      $height: 16px;
      $width: 9px;
      $borderColor: #555;
      position: relative;
      left: -5px;
      margin-left: $width + 5;
      content: '';
      transform: rotate(45deg);
      height: $height;
      width: $width;
      margin-bottom: 8px;
      border-bottom: $borderWidth solid $borderColor;
      border-right: $borderWidth solid $borderColor;
    }
  }
  &.--disabled {
    opacity: .6;
    cursor: default;
  }
  &.--highlighted {
    // background-color: $color-gray-14;
    &:before {
      opacity: 1;
    }
  }
}

.empty {
  margin-right: auto;
  margin-left: auto;
  small {
    margin-bottom: 0;
  }
}

.loader {
  position: relative;
  height: 50px;
}

</style>
